import { useEffect, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAppStore } from '@/stores/app'
import { usePermissionStore } from '@/stores/permission'
import { authStorage } from '@/lib/auth-storage'
import { AuthEvents } from '@/lib/auth-events'
import { getErrorMessage, isAuthExpiredError } from '@/lib/error'
import { authApi } from '@/api/auth'
import { Button } from '@/components/ui/button'

interface AuthGuardProps {
	children: React.ReactNode
}

export function AuthGuard({ children }: AuthGuardProps) {
	const location = useLocation()
	const navigate = useNavigate()
	const user = useAppStore((state) => state.user.user)
	const setUser = useAppStore((state) => state.user.setUser)
	const logout = useAppStore((state) => state.user.logout)
	const [isLoading, setIsLoading] = useState(true)
	const [isAuthenticated, setIsAuthenticated] = useState(false)
	const [authInitError, setAuthInitError] = useState<string | null>(null)

	// 使用 ref 跟踪当前 location，避免 stale closure 问题
	const locationRef = useRef(location)
	useEffect(() => {
		locationRef.current = location
	}, [location])

	// 使用 ref 防止 StrictMode 双重调用
	const initRef = useRef(false)

	// 初始化：检查 token 并获取用户信息
	useEffect(() => {
		const initAuth = async () => {
			// 防止 StrictMode 双重调用
			if (initRef.current) return
			initRef.current = true

			const token = authStorage.getToken()

			if (!token) {
				setIsLoading(false)
				return
			}

			// 如果已有用户信息，直接使用，不再请求
			if (user) {
				setIsAuthenticated(true)
				setIsLoading(false)
				AuthEvents.reset()
				return
			}

			try {
				setAuthInitError(null)
				// 获取最新用户信息
				const profile = await authApi.getProfile()
				setUser({
					id: String(profile.id),
					username: profile.username,
					email: profile.email,
					role: profile.is_super_admin ? 'admin' : 'operator',
					roleName: profile.is_super_admin ? '超级管理员' : '用户',
					permissions: [], // 权限通过 permission store 获取
					avatar: profile.avatar,
					is_super_admin: profile.is_super_admin,
					crmeb_uid: profile.crmeb_uid,
				})
				// 获取用户权限
				await usePermissionStore.getState().fetchPermissions()
				setIsAuthenticated(true)
				// 重置 AuthEvents 状态，允许下次 Token 过期时再次触发
				AuthEvents.reset()
			} catch (error) {
				if (isAuthExpiredError(error)) {
					// 登录态失效才清除 token；网络或服务端初始化失败保留 token 供重试。
					authStorage.removeToken()
					logout()
					return
				}
				setAuthInitError(getErrorMessage(error, '初始化登录状态失败'))
			} finally {
				setIsLoading(false)
			}
		}

		initAuth()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []) // 只在组件挂载时执行一次

	// 监听 Token 过期事件
	useEffect(() => {
		const unsubscribe = AuthEvents.onSessionExpired(() => {
			const currentPath = locationRef.current.pathname + locationRef.current.search
			// 清除 Zustand 内存状态
			logout()
			// 使用 authStorage 删除 token
			authStorage.removeToken()
			// 清除旧的持久化数据（兼容性）
			localStorage.removeItem('carrot-admin-store')
			navigate(`/login?redirect=${encodeURIComponent(currentPath)}&reason=expired`)
		})
		return unsubscribe
	}, [logout, navigate])

	// 加载中
	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
			</div>
		)
	}

	if (authInitError) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-muted/20 px-4">
				<div className="w-full max-w-sm rounded-lg border bg-background p-6 shadow-sm">
					<div className="space-y-2">
						<h1 className="text-lg font-semibold">登录状态初始化失败</h1>
						<p className="text-sm text-muted-foreground">{authInitError}</p>
					</div>
					<div className="mt-6 flex justify-end gap-2">
						<Button
							type="button"
							variant="outline"
							onClick={() => {
								authStorage.removeToken()
								logout()
								navigate('/login', { replace: true })
							}}
						>
							重新登录
						</Button>
						<Button type="button" onClick={() => window.location.reload()}>
							重试
						</Button>
					</div>
				</div>
			</div>
		)
	}

	// 检查是否已登录
	if (!isAuthenticated) {
		// 重定向到登录页，并记录原路径
		return <Navigate to="/login" state={{ from: location }} replace />
	}

	return <>{children}</>
}
